/**
 * Patch operations — extract, generate, apply patches for skill improvement.
 *
 * Patches allow editing deployed assets from remote repos and contributing
 * changes back upstream via PR.
 */

import { spawnSync } from "node:child_process";
import { cpSync, existsSync, mkdirSync, readdirSync, readFileSync, writeFileSync } from "node:fs";
import { basename, dirname, join } from "node:path";
import type { CatalogEntry } from "./models.js";

/**
 * Copy an asset's source directory into a patch working directory for editing.
 *
 * @param entry - The catalog entry to extract
 * @param patchDir - Base directory where extracted copies are placed
 * @returns Absolute path to the extracted copy (`<patchDir>/<kind>-<name>`)
 * @docLink packages/core/api-reference#extract-for-patch
 */
export function extractForPatch(entry: CatalogEntry, patchDir: string): string {
  const ref = `${entry.kind}-${entry.name}`;
  const dest = join(patchDir, ref);
  mkdirSync(dest, { recursive: true });

  // Copy the asset's source directory (or file) to the patch dir
  const sourceDir = dirname(entry.source);
  const sourceName = basename(entry.source);

  // If source is a file in a directory (e.g., skills/overview/SKILL.md), copy the whole dir
  if (existsSync(sourceDir)) {
    cpSync(sourceDir, dest, { recursive: true });
  }

  return dest;
}

/**
 * Generate a unified diff between an original and patched directory using `diff -ruN`.
 *
 * @param originalDir - Unmodified copy of the asset directory
 * @param patchedDir - Modified copy of the asset directory
 * @returns Patch content string, or `null` if there are no differences (or on error)
 * @docLink packages/core/api-reference#generate-patch
 */
export function generatePatch(originalDir: string, patchedDir: string): string | null {
  const r = spawnSync("diff", ["-ruN", originalDir, patchedDir], {
    encoding: "utf8",
    stdio: "pipe",
  });

  // diff returns 0 if no differences, 1 if differences, 2 on error
  if (r.status === 0) return null; // no differences
  if (r.status === 1) return r.stdout;
  return null; // error
}

/**
 * Apply a unified diff patch file to a directory using `patch -p1`.
 *
 * @param targetDir - Directory to apply the patch to
 * @param patchFile - Path to the `.patch` file
 * @returns `true` on success, `false` if the patch file is missing or the command fails
 * @docLink packages/core/api-reference#apply-patch
 */
export function applyPatch(targetDir: string, patchFile: string): boolean {
  if (!existsSync(patchFile)) return false;
  const r = spawnSync("patch", ["-p1", "-d", targetDir, "-i", patchFile], {
    stdio: "pipe",
  });
  return r.status === 0;
}

/**
 * Return the paths of all `.patch` files in a directory.
 *
 * @param patchDir - Directory to scan for `.patch` files
 * @returns Array of absolute paths; empty array when `patchDir` does not exist
 * @docLink packages/core/api-reference#list-patches
 */
export function listPatches(patchDir: string): string[] {
  if (!existsSync(patchDir)) return [];
  return readdirSync(patchDir)
    .filter((f) => f.endsWith(".patch"))
    .map((f) => join(patchDir, f));
}

/**
 * Write patch content to a file, creating parent directories as needed.
 *
 * @param patchPath - Destination file path (including `.patch` extension)
 * @param content - Unified diff content to write
 * @docLink packages/core/api-reference#save-patch
 */
export function savePatch(patchPath: string, content: string): void {
  mkdirSync(dirname(patchPath), { recursive: true });
  writeFileSync(patchPath, content, "utf8");
}

/**
 * Read the content of a patch file.
 *
 * @param patchPath - Path to the `.patch` file
 * @returns File content as a string, or `null` if the file does not exist
 * @docLink packages/core/api-reference#read-patch
 */
export function readPatch(patchPath: string): string | null {
  if (!existsSync(patchPath)) return null;
  return readFileSync(patchPath, "utf8");
}
